import React, { useState, useEffect } from 'react';
import { UseMutationResult } from '@tanstack/react-query';
import { Button, Form, Input, notification, Switch, Tabs, Typography, Divider } from 'antd';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { closeModal } from '@/store/modal/slice';
import {
	QuestionCircleOutlined,
	EditOutlined,
	EyeOutlined,
	MailOutlined,
	UserOutlined,
	TagOutlined,
	CopyOutlined,
} from '@ant-design/icons';
import { RefetchOptions, QueryObserverResult } from '@tanstack/react-query';
import { User } from '@/types/TAccounts';
import { selectCanAddScenario } from '@/store/scenarioCounts/selector';
import { incrementScenarioCount } from '@/store/scenarioCounts/slice';
import { FormActionType, WorkflowType } from 'src/enums/EWorkflow';

const { TextArea } = Input;
const { TabPane } = Tabs;
const { Text } = Typography;

interface EmailWorkflowFormProps {
	actionType: FormActionType;
	showAddFieldButton?: boolean; // Added back this prop
	initialValues?: any;
	refetch: (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<User, Error>>;
	mutation: UseMutationResult<unknown, Error, any, unknown>;
	scenarioIndex?: number;
	debrand: boolean;
	isNoviUser: boolean;
}

// Email preview component with better styling
const EmailPreview: React.FC<{ formValues: any }> = ({ formValues }) => {
	const { emailSubject, emailBody, ccEmails } = formValues?.emailWorkflows?.[0] || {};

	// Parse placeholders for preview
	const parseContent = (content: string) => {
		if (!content) {
			return '';
		}
		return content
			.replace(/{caller_name}/g, 'John Smith')
			.replace(/{caller_email}/g, 'john.smith@example.com');
	};

	return (
		<div className="bg-white rounded-lg shadow-lg border border-gray-200">
			<div className="bg-gray-50 p-4 border-b border-gray-200 rounded-t-lg">
				<div className="flex items-center mb-2">
					<MailOutlined className="text-blue-500 mr-2" />
					<Text strong>Email Preview</Text>
				</div>
				<div className="text-sm">
					<div className="flex items-center mb-1">
						<Text type="secondary" className="w-16">
							From:
						</Text>
						<Text>Your AI Assistant &lt;assistant@yourdomain.com&gt;</Text>
					</div>
					<div className="flex items-center mb-1">
						<Text type="secondary" className="w-16">
							To:
						</Text>
						<Text>Caller's email (auto-captured)</Text>
					</div>
					{ccEmails && (
						<div className="flex items-center mb-1">
							<Text type="secondary" className="w-16">
								CC:
							</Text>
							<Text>{parseContent(ccEmails)}</Text>
						</div>
					)}
					<div className="flex items-center mt-3">
						<Text type="secondary" className="w-16">
							Subject:
						</Text>
						<Text strong>{parseContent(emailSubject) || 'Email Subject'}</Text>
					</div>
				</div>
			</div>

			<div className="p-6 bg-white rounded-b-lg">
				<div className="whitespace-pre-line">
					{parseContent(emailBody) || 'Email body content will appear here...'}
				</div>
			</div>
		</div>
	);
};

// Small component to display available placeholders
const PlaceholderTags: React.FC = () => (
	<div className="mb-4">
		<Text type="secondary" className="text-sm block mb-2">
			Available placeholders:
		</Text>
		<div className="flex flex-wrap gap-2">
			<Button type="dashed" size="small" icon={<UserOutlined />} className="flex items-center">
				{'{caller_name}'}
			</Button>
			<Button type="dashed" size="small" icon={<MailOutlined />} className="flex items-center">
				{'{caller_email}'}
			</Button>
		</div>
	</div>
);

const EmailWorkflowForm: React.FC<EmailWorkflowFormProps> = ({
	actionType,
	initialValues,
	refetch,
	mutation,
	scenarioIndex,
}) => {
	const dispatch = useAppDispatch();
	const [form] = Form.useForm();
	const [activeTab, setActiveTab] = useState('compose');
	const [formValues, setFormValues] = useState<any>(initialValues);
	const canAddScenario = useAppSelector(selectCanAddScenario);

	useEffect(() => {
		if (initialValues) {
			form.setFieldsValue(initialValues);
			setFormValues(initialValues);
		}
	}, [initialValues, form]);

	// Update preview whenever form values change
	const handleValuesChange = (allValues: any) => {
		setFormValues(allValues);
	};

	const onFinish = async (values: any) => {
		try {
			const previousWorkflowData = values.emailWorkflows[0].previousWorkflowData || [];

			// Prepare new workflow data
			const newWorkflow = {
				description: values.emailWorkflows[0].description,
				emailSubject: values.emailWorkflows[0].emailSubject,
				emailBody: values.emailWorkflows[0].emailBody,

				ccEmails: values.emailWorkflows[0].ccEmails,
				askForPermissionOnEmail: values.emailWorkflows[0].askForPermissionOnEmail ?? true,
			};

			let updatedWorkflows;
			if (actionType === FormActionType.EDIT && scenarioIndex !== undefined) {
				updatedWorkflows = [...previousWorkflowData];
				updatedWorkflows[scenarioIndex] = newWorkflow;
			} else {
				updatedWorkflows = [...previousWorkflowData, newWorkflow];
			}

			// Submit the mutation
			await mutation.mutateAsync({ emailWorkflows: updatedWorkflows });

			// Show success notification
			notification.success({
				message: `Email workflow ${actionType === FormActionType.ADD ? 'added' : 'updated'} successfully.`,
				duration: 3,
			});

			// Refetch and close modal
			refetch();
			dispatch(closeModal());

			// After successful mutation, increment the count for new scenarios
			if (actionType === FormActionType.ADD) {
				dispatch(incrementScenarioCount(WorkflowType.EMAIL));
			}
		} catch {
			notification.error({
				message: 'Something went wrong',
				description: 'Please try again later.',
			});
		}
	};

	const handleAddForm = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		if (!canAddScenario) {
			notification.error({
				message: 'Scenario limit reached',
				description: 'Max scenarios (100) reached. Please Delete existing to add more.',
			});
			return;
		}
	};

	return (
		<div className="bg-white">
			<Tabs
				activeKey={activeTab}
				onChange={setActiveTab}
				className="email-workflow-tabs"
				tabBarStyle={{ padding: '0 1rem', borderBottom: '1px solid #f0f0f0' }}
				tabBarGutter={24}
			>
				<TabPane
					tab={
						<span className="text-base flex items-center">
							<EditOutlined className="mr-2" />
							Compose
						</span>
					}
					key="compose"
				>
					<div className="p-6">
						<Form
							form={form}
							layout="vertical"
							onFinish={onFinish}
							onValuesChange={handleValuesChange}
							initialValues={{
								emailWorkflows: [
									{
										askForPermissionOnEmail: true,
									},
								],
							}}
							className="email-workflow-form"
						>
							<Form.Item
								name={['emailWorkflows', 0, 'description']}
								label={
									<div className="flex items-center text-base font-medium">
										<TagOutlined className="mr-2 text-blue-500" />
										<span>Scenario Description</span>
										<QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
									</div>
								}
								tooltip="Describe when the AI should send this email during a call"
								rules={[{ required: true, message: 'Please enter a scenario description' }]}
							>
								<TextArea
									rows={2}
									placeholder="E.g., When a caller requests pricing information"
									className="rounded-md"
								/>
							</Form.Item>

							<div className="bg-blue-50 p-4 rounded-lg mb-6">
								<div className="flex items-start">
									<div className="text-blue-500 mr-3 mt-1">
										<MailOutlined />
									</div>
									<div>
										<Text strong className="text-blue-700">
											Email Configuration
										</Text>
										<Text className="block text-sm text-blue-600 mt-1">
											The caller's email will be automatically captured during the call. You can
											focus on creating the email content.
										</Text>
									</div>
								</div>
							</div>

							<Divider orientation="left">
								<div className="flex items-center">
									<MailOutlined className="mr-2 text-blue-500" />
									<span>Email Content</span>
								</div>
							</Divider>

							<PlaceholderTags />

							<Form.Item
								name={['emailWorkflows', 0, 'emailSubject']}
								label={
									<div className="flex items-center font-medium">
										<span>Email Subject</span>
									</div>
								}
								rules={[{ required: true, message: 'Please enter an email subject' }]}
							>
								<Input
									placeholder="E.g., Information You Requested"
									className="rounded-md"
									prefix={<CopyOutlined className="text-gray-400" />}
								/>
							</Form.Item>

							<Form.Item
								name={['emailWorkflows', 0, 'emailBody']}
								label={
									<div className="flex items-center font-medium">
										<span>Email Body</span>
									</div>
								}
								tooltip="Use {caller_name} and {caller_email} as placeholders that will be replaced automatically"
								rules={[{ required: true, message: 'Please enter email content' }]}
							>
								<TextArea
									rows={6}
									placeholder="Dear {caller_name},

Thank you for your call today. Here is the information you requested...

Best regards,
Your Company Team"
									className="rounded-md"
								/>
							</Form.Item>

							<Form.Item
								name={['emailWorkflows', 0, 'ccEmails']}
								label={
									<div className="flex items-center font-medium">
										<span>CC Recipients (Optional)</span>
									</div>
								}
								tooltip="Email addresses to CC, separated by commas"
							>
								<Input
									placeholder="E.g., team@example.com, manager@example.com"
									className="rounded-md"
									prefix={<MailOutlined className="text-gray-400" />}
								/>
							</Form.Item>

							<Divider orientation="left">
								<div className="flex items-center">
									<TagOutlined className="mr-2 text-blue-500" />
									<span>Options</span>
								</div>
							</Divider>

							<Form.Item
								valuePropName="checked"
								name={['emailWorkflows', 0, 'askForPermissionOnEmail']}
								label={
									<div className="flex items-center font-medium">
										<span>Ask for Permission</span>
									</div>
								}
								tooltip="If enabled, the AI will ask the caller for permission before sending the email"
							>
								<Switch />
							</Form.Item>

							<Form.Item hidden name={['emailWorkflows', 0, 'previousWorkflowData']}>
								<Input />
							</Form.Item>

							<div className="flex justify-end gap-3 mt-8">
								<Button onClick={() => dispatch(closeModal())} size="large">
									Cancel
								</Button>
								<Button
									type="primary"
									htmlType="submit"
									loading={mutation.isPending}
									size="large"
									onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleAddForm(e)}
								>
									{actionType === FormActionType.ADD ? 'Add Email Workflow' : 'Save Changes'}
								</Button>
							</div>
						</Form>
					</div>
				</TabPane>

				<TabPane
					tab={
						<span className="text-base flex items-center">
							<EyeOutlined className="mr-2" />
							Preview
						</span>
					}
					key="preview"
				>
					<div className="p-6">
						<EmailPreview formValues={formValues} />
					</div>
				</TabPane>
			</Tabs>
		</div>
	);
};

export default EmailWorkflowForm;
